import React, { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import AgeGate from "./AgeGate";

const AGE_KEY = "ne_age_confirmed_v1";

/**
 * AgeGateGuard – per-side aldersgate for eksplisitt innhold.
 *
 * Vises hvis:
 *   1. Bruker ikke er logget inn, OG
 *   2. Bruker ikke har bekreftet alder i nettleseren (`localStorage`), OG
 *   3. Trafikken kommer fra ekstern lenke (annerledes referrer eller `?source`-flag)
 *
 * Innloggede brukere har allerede bekreftet 18+ ved registrering – ingen gate.
 *
 * Bruk:
 *   <AgeGateGuard>
 *     <AlbumView />
 *   </AgeGateGuard>
 */
export default function AgeGateGuard({ children }) {
  const { user, loading } = useAuth();
  const [showGate, setShowGate] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (loading) return;

    // Innloggede brukere: ingen gate
    if (user && user !== false) {
      setShowGate(false);
      setChecked(true);
      return;
    }

    // Allerede bekreftet i denne nettleseren
    try {
      if (localStorage.getItem(AGE_KEY) === "yes") {
        setShowGate(false);
        setChecked(true);
        return;
      }
    } catch (e) {
      // localStorage utilgjengelig – fall through til gate
    }

    // Sjekk om besøk kommer fra ekstern lenke
    const isExternal = isExternalEntry();
    setShowGate(isExternal);
    setChecked(true);
  }, [user, loading]);

  if (loading || !checked) {
    return (
      <div className="min-h-screen bg-[var(--noir)]" data-testid="age-gate-loading" />
    );
  }

  if (showGate) {
    return (
      <>
        {/* Innholdet er rendert under, men blurret for trygghets skyld */}
        <div className="guest-blur-target pointer-events-none select-none">
          {children}
        </div>
        <AgeGate onConfirm={() => setShowGate(false)} />
      </>
    );
  }

  return <>{children}</>;
}

/**
 * Detekterer om dette er en ekstern «entry» (dyplenke fra Reddit/Twitter osv.)
 * Strategi:
 *   - URL har `?source=share` eller `?ref=...` → eksternt
 *   - document.referrer er fra annet domene (eller tom = direkte klikk i app)
 *   - Tom referrer + navigation type «navigate» (ikke reload/back) = direkte URL-besøk → behandle som ekstern
 */
function isExternalEntry() {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has("source") || url.searchParams.has("ref")) return true;

    const referrer = document.referrer || "";
    if (!referrer) {
      // Direkte URL-besøk (skrev URL inn / klikket lenke fra annen app)
      const navType = performance.getEntriesByType("navigation")[0]?.type;
      if (navType === "navigate") return true;
      return false;
    }

    const refHost = new URL(referrer).host;
    return refHost !== window.location.host;
  } catch {
    return false;
  }
}
